import type { ColDef, GridApi, GridReadyEvent, RowClickedEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { PlusIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { SettingsListDetailLayout } from '@/components/common/SettingsListDetailLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useGridDeselectOnClose } from '@/hooks/useGridDeselectOnClose';
import type { WBS } from '../data/databaseSchema';
import {
  agGridCSSClasses,
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridSettingsContainerStyles,
  agGridTheme,
  agGridThemeStyles,
} from '../theme/agGridTheme';
import { WbsDrawer } from './WbsDrawer';

type DetailPanelMode = 'empty' | 'selected' | 'edit' | 'add';

interface WbsListProps {
  wbsItems: WBS[];
  onEdit?: (wbs: WBS) => void;
  onDelete?: (wbsId: string) => void;
  onAdd?: () => void;
  isLoading?: boolean;
}

export const WbsList: React.FC<WbsListProps> = ({
  wbsItems,
  onEdit,
  onDelete,
  onAdd,
  isLoading = false,
}) => {
  const gridRef = useRef<AgGridReact>(null);
  const [selectedWbsItems, setSelectedWbsItems] = useState<WBS[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const justSavedRef = useRef(false);
  const [panelMode, setPanelMode] = useState<DetailPanelMode>('empty');
  const [selectedWbs, setSelectedWbs] = useState<WBS | null>(null);
  const [editingWbs, setEditingWbs] = useState<WBS | null>(null);

  const handleAddClick = useCallback(() => {
    setEditingWbs(null);
    setPanelMode('add');
  }, []);

  const handleDrawerSave = useCallback(
    (wbs: WBS) => {
      if (onEdit && panelMode === 'edit') {
        onEdit(wbs);
      } else if (onAdd && panelMode === 'add') {
        onAdd();
      }
    },
    [onEdit, onAdd, panelMode]
  );

  const handleFormClose = useCallback(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      setEditingWbs(null);
      return;
    }
    setPanelMode((mode) => (mode === 'edit' ? 'selected' : 'empty'));
    setEditingWbs(null);
  }, []);

  const handleViewClose = useCallback(() => {
    setPanelMode('empty');
    setSelectedWbs(null);
    setEditingWbs(null);
  }, []);

  const handleRowClicked = useCallback((event: RowClickedEvent<WBS>) => {
    if (!event.data) return;
    setSelectedWbs(event.data);
    setPanelMode('selected');
    setEditingWbs(null);
  }, []);

  const handleDelete = useCallback(
    (wbs: WBS) => {
      if (onDelete) {
        onDelete(wbs.id);
        setSelectedWbs(null);
        setPanelMode('empty');
      }
    },
    [onDelete]
  );

  // Column definitions for the WBS grid
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: 'Code',
        field: 'task_code',
        width: 150,
        minWidth: 120,
        maxWidth: 200,
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        comparator: (a: unknown, b: unknown) => {
          const sa = a == null ? '' : String(a);
          const sb = b == null ? '' : String(b);
          return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' });
        },
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sort: 'asc',
      },
      {
        headerName: 'Name',
        field: 'name',
        flex: 1,
        minWidth: 200,
        maxWidth: 400,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
      },
      {
        headerName: 'Project Type',
        field: 'project_type',
        width: 170,
        minWidth: 150,
        maxWidth: 180,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        getQuickFilterText: (params) => String(params.data?.project_type ?? ''),
        cellRenderer: (params: { value?: string }) => {
          const t = String(params.value ?? '')
            .trim()
            .toLowerCase();
          if (t === 'internal') {
            return (
              <span
                title="Internal"
                className="inline-block rounded px-2 py-0.5 text-xs font-semibold bg-blue-500/20 text-primary-700"
              >
                Int
              </span>
            );
          }
          if (t === 'external') {
            return (
              <span
                title="External"
                className="inline-block rounded px-2 py-0.5 text-xs font-semibold bg-yellow-500/20 text-text-primary"
              >
                Ext
              </span>
            );
          }
          if (!t) return '';
          const raw = String(params.value).trim();
          return <span>{raw.charAt(0).toUpperCase() + raw.slice(1)}</span>;
        },
      },
      {
        headerName: 'Billable',
        field: 'billable',
        width: 130,
        minWidth: 120,
        maxWidth: 150,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        cellRenderer: (params: any) => {
          if (params.value) return <span className="rounded-lg bg-success text-white p-2">✓</span>;
          return '-';
        },
      },
    ],
    []
  );

  // Search functionality
  const handleSearch = useCallback(
    (searchValue: string) => {
      setSearchTerm(searchValue);
      if (gridApi) {
        gridApi.setGridOption('quickFilterText', searchValue);
      }
    },
    [gridApi]
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    if (gridApi) {
      gridApi.setGridOption('quickFilterText', '');
    }
  }, [gridApi]);

  // Export functionality
  const handleExportCSV = useCallback(() => {
    if (gridApi) {
      gridApi.exportDataAsCsv({
        fileName: 'wbs-items.csv',
        columnKeys: ['id', 'projectId', 'name', 'code'],
      });
    }
  }, [gridApi]);

  // Grid event handlers
  const handleGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  const handleFirstDataRendered = useCallback(() => {
    // Data rendered
  }, []);

  const handleRowDataUpdated = useCallback(() => {
    // Row data updated
  }, []);

  const handleSelectionChanged = useCallback(() => {
    if (gridApi) {
      const selectedNodes = gridApi.getSelectedNodes();
      const selectedData = selectedNodes.map((node) => node.data);
      setSelectedWbsItems(selectedData);
    }
  }, [gridApi]);

  // Grid options
  const gridOptions = {
    ...agGridDefaultOptions,
    theme: agGridTheme,
    suppressContextMenu: true,
    onGridReady: handleGridReady,
    onFirstDataRendered: handleFirstDataRendered,
    onRowDataUpdated: handleRowDataUpdated,
    onSelectionChanged: handleSelectionChanged,
    onRowClicked: handleRowClicked,
    rowSelection: { mode: 'singleRow' as const, checkboxes: false },
    quickFilterText: searchTerm,
    defaultColDef: {
      ...agGridDefaultColDef,
      sortable: true,
    },
    rowNumbers: { width: 30, minWidth: 25 },
  };

  const detailOpen = panelMode !== 'empty';

  useGridDeselectOnClose(gridRef, detailOpen);

  return (
    <SettingsListDetailLayout
      detailOpen={detailOpen}
      detail={
        panelMode === 'edit' || panelMode === 'add' ? (
          <WbsDrawer
            isOpen={true}
            onClose={handleFormClose}
            onSave={handleDrawerSave}
            onEntrySaved={() => {
              justSavedRef.current = true;
              setPanelMode('empty');
              setSelectedWbs(null);
              onAdd?.();
            }}
            wbs={editingWbs}
            isEdit={panelMode === 'edit'}
          />
        ) : panelMode === 'selected' && selectedWbs ? (
          <WbsDrawer
            key={selectedWbs.id}
            isOpen={true}
            readOnly
            wbs={selectedWbs}
            onClose={handleViewClose}
            onSave={() => {}}
            onEditRequest={() => {
              setEditingWbs(selectedWbs);
              setPanelMode('edit');
            }}
            onDeleteRequest={onDelete ? () => handleDelete(selectedWbs) : undefined}
          />
        ) : null
      }
      list={
        <div className="flex min-h-0 flex-1 flex-col p-2">
          <div className="mb-6 flex shrink-0 items-center justify-between">
            <div className="flex items-center gap-3 bg-white">
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Search WBS items..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pr-8"
                />
                {searchTerm && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    className="absolute top-1/2 right-1 -translate-y-1/2"
                    onClick={handleClearSearch}
                    title="Clear search"
                  >
                    ×
                  </Button>
                )}
              </div>

              {selectedWbsItems.length > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 text-sm text-primary-600">
                  {selectedWbsItems.length} selected
                </div>
              )}

              <Button type="button" variant="secondary" onClick={handleExportCSV}>
                Export CSV
              </Button>
            </div>
            <Button type="button" onClick={handleAddClick}>
              <PlusIcon data-icon="inline-start" />
              Add WBS Item
            </Button>
          </div>

          {!isLoading && wbsItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
              <div className="text-5xl text-text-muted">🌳</div>
              <div className="text-lg font-medium text-text-secondary">No WBS items found</div>
              <div className="text-sm text-text-muted">Add your first WBS item to get started</div>
              <Button type="button" className="mt-2" onClick={handleAddClick}>
                <PlusIcon data-icon="inline-start" />
                Add WBS Item
              </Button>
            </div>
          ) : (
            <>
              <style>{agGridThemeStyles}</style>
              <div
                className="ag-theme-material min-h-0 flex-1"
                style={agGridSettingsContainerStyles}
              >
                <AgGridReact
                  ref={gridRef}
                  rowData={wbsItems}
                  columnDefs={columnDefs}
                  defaultColDef={{ ...agGridDefaultColDef, sortable: true }}
                  gridOptions={gridOptions}
                  animateRows={true}
                  enableCellTextSelection={true}
                  ensureDomOrder={true}
                />

                {isLoading && (
                  <div className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-lg bg-surface-card px-8 py-4 text-sm font-medium text-text-secondary shadow-md">
                    <Spinner className="size-4" />
                    Loading WBS items...
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      }
    />
  );
};
